import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Megaphone, 
  TrendingUp, 
  Users, 
  Globe, 
  Check, 
  Loader2,
  Building2,
  Mail,
  Phone,
  Link,
  Target,
  Sparkles,
  BarChart3,
  Eye,
  MousePointer,
  ArrowRight,
  Coins,
  Trash2,
  Pause,
  Play,
  AlertTriangle,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvertisePage() {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [advertiserId, setAdvertiserId] = useState(searchParams.get('advertiser_id') || null);
  const [advertiserStatus, setAdvertiserStatus] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeEmail, setRemoveEmail] = useState('');
  const [removing, setRemoving] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    business_type: 'financial_services',
    description: '',
    target_audience: ''
  });

  useEffect(() => {
    loadPackages();
    
    // Check for payment session
    const sessionId = searchParams.get('session_id');
    const advId = searchParams.get('advertiser_id');
    
    if (sessionId && advId) {
      checkPaymentStatus(advId, sessionId);
    } else if (advId) {
      loadAdvertiserStatus(advId);
    }
  }, [searchParams]);

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/advertisers/packages`);
      setPackages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Load packages error:', error);
      setLoading(false);
    }
  };

  const loadAdvertiserStatus = async (advId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/advertisers/status/${advId}`);
      setAdvertiserStatus(response.data);
      setApplicationSubmitted(true);
    } catch (error) {
      console.error('Load status error:', error);
    }
  };

  const checkPaymentStatus = async (advId, sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/advertisers/${advId}/checkout/status/${sessionId}`
        );

        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your ad package is now active.');
          loadAdvertiserStatus(advId);
          return;
        }

        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('Payment check error:', error);
      }
    };

    poll();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.contact_name || !formData.email || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/advertisers/apply`, formData);
      
      setAdvertiserId(response.data.advertiser_id);
      setApplicationSubmitted(true);
      setAdvertiserStatus({ status: 'pending', ...response.data });
      toast.success(response.data.message);
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAccount = async () => {
    if (!removeEmail) {
      toast.error('Please enter your email to confirm');
      return;
    }

    setRemoving(true);
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/advertisers/${advertiserId}`,
        {
          data: {
            email: removeEmail,
            confirm: true
          }
        }
      );
      
      toast.success(response.data.message);
      setShowRemoveModal(false);
      setAdvertiserId(null);
      setAdvertiserStatus(null);
      setApplicationSubmitted(false);
      setRemoveEmail('');
      
    } catch (error) {
      console.error('Remove error:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove account');
    } finally {
      setRemoving(false);
    }
  };

  const handlePauseAccount = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/advertisers/${advertiserId}/pause`
      );
      
      toast.success(response.data.message);
      setAdvertiserStatus(prev => ({ ...prev, status: response.data.status }));
      
    } catch (error) {
      console.error('Pause error:', error);
      toast.error(error.response?.data?.detail || 'Failed to pause account');
    }
  };

  const handleSelectPackage = async (packageId) => {
    if (!advertiserId) {
      toast.error('Please submit your application first');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/advertisers/${advertiserId}/subscribe`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Package selection error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process package selection');
    }
  };

  const businessTypes = [
    { value: 'financial_services', label: 'Financial Services' },
    { value: 'fintech', label: 'Fintech' },
    { value: 'banking', label: 'Banking' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'investments', label: 'Investments' },
    { value: 'remittance', label: 'Remittance/Money Transfer' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'education', label: 'Education' },
    { value: 'health', label: 'Health & Wellness' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge className="bg-white/20 text-white mb-4 px-4 py-1">
              <Megaphone className="w-4 h-4 mr-2" />
              Advertise with Susu Money
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Reach Millions of Savers Worldwide
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Connect your brand with our growing community of financially-conscious users 
              across 50+ countries. Trusted platform with engaged audience.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { icon: Users, value: '5,000+', label: 'Active Users' },
                { icon: Globe, value: '50+', label: 'Countries' },
                { icon: Eye, value: '100K+', label: 'Monthly Views' },
                { icon: TrendingUp, value: '89%', label: 'Engagement Rate' }
              ].map((stat, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-4">
                  <stat.icon className="w-6 h-6 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Building2 className="w-5 h-5 mr-2 text-indigo-600" />
                  {applicationSubmitted ? 'Application Status' : 'Become an Advertiser'}
                </CardTitle>
                <CardDescription>
                  {applicationSubmitted 
                    ? 'Your application has been submitted'
                    : 'Fill out the form below to get started'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {applicationSubmitted ? (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-xl ${
                      advertiserStatus?.status === 'active' 
                        ? 'bg-green-50 border border-green-200'
                        : advertiserStatus?.status === 'pending'
                        ? 'bg-amber-50 border border-amber-200'
                        : advertiserStatus?.status === 'paused'
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-stone-50 border border-stone-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">Status</span>
                        <Badge className={
                          advertiserStatus?.status === 'active' 
                            ? 'bg-green-100 text-green-700'
                            : advertiserStatus?.status === 'pending'
                            ? 'bg-amber-100 text-amber-700'
                            : advertiserStatus?.status === 'paused'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-stone-100 text-stone-700'
                        }>
                          {advertiserStatus?.status?.toUpperCase()}
                        </Badge>
                      </div>
                      {advertiserStatus?.package && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-stone-600">Package</span>
                          <span className="font-medium">{advertiserStatus.package}</span>
                        </div>
                      )}
                    </div>
                    
                    {advertiserStatus?.status === 'pending' && (
                      <p className="text-sm text-stone-600">
                        We'll review your application and contact you within 24-48 hours. 
                        In the meantime, you can select a package below.
                      </p>
                    )}
                    
                    {advertiserStatus?.status === 'active' && (
                      <Button 
                        className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => window.location.href = `/advertise/dashboard?advertiser_id=${advertiserId}`}
                      >
                        Go to Advertiser Dashboard
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    )}

                    {/* Account Management Options */}
                    <div className="pt-4 border-t border-stone-200">
                      <p className="text-sm text-stone-500 mb-3">Account Management</p>
                      <div className="flex gap-2">
                        {(advertiserStatus?.status === 'active' || advertiserStatus?.status === 'paused') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePauseAccount}
                            className="flex-1 rounded-lg"
                            data-testid="pause-account-btn"
                          >
                            {advertiserStatus?.status === 'paused' ? (
                              <>
                                <Play className="w-4 h-4 mr-1" />
                                Resume
                              </>
                            ) : (
                              <>
                                <Pause className="w-4 h-4 mr-1" />
                                Pause
                              </>
                            )}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowRemoveModal(true)}
                          className="flex-1 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          data-testid="remove-account-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove Account
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitApplication} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="business_name">Business Name *</Label>
                        <Input
                          id="business_name"
                          name="business_name"
                          value={formData.business_name}
                          onChange={handleInputChange}
                          placeholder="Your company name"
                          className="rounded-xl mt-1"
                          required
                          data-testid="business-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="contact_name">Contact Name *</Label>
                        <Input
                          id="contact_name"
                          name="contact_name"
                          value={formData.contact_name}
                          onChange={handleInputChange}
                          placeholder="Your name"
                          className="rounded-xl mt-1"
                          required
                          data-testid="contact-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@company.com"
                          className="rounded-xl mt-1"
                          required
                          data-testid="email-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1 234 567 8900"
                          className="rounded-xl mt-1"
                          data-testid="phone-input"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          placeholder="https://yoursite.com"
                          className="rounded-xl mt-1"
                          data-testid="website-input"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label htmlFor="business_type">Business Type *</Label>
                        <select
                          id="business_type"
                          name="business_type"
                          value={formData.business_type}
                          onChange={handleInputChange}
                          className="w-full rounded-xl mt-1 border border-stone-200 p-2"
                          data-testid="business-type-select"
                        >
                          {businessTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="col-span-2">
                        <Label htmlFor="description">Tell us about your business *</Label>
                        <Textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="What products or services do you offer? What makes you unique?"
                          className="rounded-xl mt-1 min-h-[100px]"
                          required
                          data-testid="description-input"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label htmlFor="target_audience">Target Audience</Label>
                        <Textarea
                          id="target_audience"
                          name="target_audience"
                          value={formData.target_audience}
                          onChange={handleInputChange}
                          placeholder="Who do you want to reach? (e.g., young professionals, families, small business owners)"
                          className="rounded-xl mt-1"
                          data-testid="target-audience-input"
                        />
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 py-6"
                      data-testid="submit-application-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Megaphone className="mr-2 w-4 h-4" />
                          Submit Application
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Packages */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2">
                Advertising Packages
              </h2>
              <p className="text-stone-600">
                Choose a package that fits your marketing goals
              </p>
              {!advertiserId && (
                <p className="text-amber-600 text-sm mt-2 flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-2 animate-pulse"></span>
                  Fill out the application form first to select a package
                </p>
              )}
            </div>
            
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
              >
                <Card className={`border-2 transition-all hover:shadow-lg ${
                  pkg.id === 'growth' 
                    ? 'border-indigo-500 shadow-indigo-100' 
                    : 'border-stone-200'
                }`}>
                  {pkg.id === 'growth' && (
                    <div className="bg-indigo-500 text-white text-center py-1 text-sm font-medium">
                      <Sparkles className="w-4 h-4 inline mr-1" />
                      Most Popular
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-stone-900">{pkg.name}</h3>
                        <p className="text-stone-500 text-sm">{pkg.duration_days} days</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-stone-900">${pkg.price}</div>
                        <div className="text-sm text-stone-500">/month</div>
                      </div>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-sm text-stone-600">
                          <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      onClick={() => {
                        if (!advertiserId) {
                          toast.info('Please fill out the application form first');
                          document.getElementById('business_name')?.focus();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        } else {
                          handleSelectPackage(pkg.id);
                        }
                      }}
                      className={`w-full rounded-xl ${
                        !advertiserId 
                          ? 'bg-amber-500 hover:bg-amber-600'
                          : pkg.id === 'growth'
                          ? 'bg-indigo-600 hover:bg-indigo-700'
                          : 'bg-stone-900 hover:bg-stone-800'
                      }`}
                      data-testid={`select-package-${pkg.id}`}
                    >
                      {!advertiserId ? '← Fill Application First' : `Select ${pkg.name}`}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Benefits Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-center text-stone-900 mb-8">
            Why Advertise with Susu Money?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: 'Targeted Audience',
                description: 'Reach financially-conscious users actively managing their savings and investments.'
              },
              {
                icon: Globe,
                title: 'Global Reach',
                description: 'Connect with users across 50+ countries, particularly strong in African and diaspora markets.'
              },
              {
                icon: BarChart3,
                title: 'Detailed Analytics',
                description: 'Track impressions, clicks, and conversions with our comprehensive analytics dashboard.'
              },
              {
                icon: Users,
                title: 'Engaged Community',
                description: 'Our users are active, engaged, and trust our platform for their financial journey.'
              },
              {
                icon: Coins,
                title: 'Affordable Pricing',
                description: 'Competitive rates starting at just $49/month. No hidden fees or long-term contracts.'
              },
              {
                icon: Sparkles,
                title: 'Premium Placements',
                description: 'Multiple ad placements across landing pages, dashboards, and club pages.'
              }
            ].map((benefit, i) => (
              <Card key={i} className="border-stone-200">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-stone-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>
      </div>
      {/* Remove Account Confirmation Modal */}
      <AnimatePresence>
        {showRemoveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRemoveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="remove-account-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center text-red-600">
                  <AlertTriangle className="w-6 h-6 mr-2" />
                  <h3 className="text-lg font-semibold">Remove Account</h3>
                </div>
                <button
                  onClick={() => setShowRemoveModal(false)}
                  className="text-stone-400 hover:text-stone-600"
                  data-testid="close-modal-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-stone-600 mb-4">
                Are you sure you want to remove your advertiser account? This will:
              </p>

              <ul className="text-sm text-stone-600 mb-6 space-y-2">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                  Deactivate all your active advertisements
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                  Remove your business from our platform
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                  Remaining package time will not be refunded
                </li>
              </ul>

              <div className="mb-4">
                <Label htmlFor="confirm_email" className="text-sm font-medium text-stone-700">
                  Enter your email to confirm
                </Label>
                <Input
                  id="confirm_email"
                  type="email"
                  value={removeEmail}
                  onChange={(e) => setRemoveEmail(e.target.value)}
                  placeholder={advertiserStatus?.email || "your@email.com"}
                  className="mt-1 rounded-xl"
                  data-testid="confirm-email-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRemoveModal(false)}
                  className="flex-1 rounded-xl"
                  data-testid="cancel-remove-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemoveAccount}
                  disabled={removing}
                  className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-remove-btn"
                >
                  {removing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Account
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
